import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { Logo, SiteTag } from "@/components/Logo";
import { useSiteInfo } from "@/hooks/useSiteInfo";
import { api } from "@/lib/api";

const HERO_IMG =
    process.env.REACT_APP_HERO_IMAGE_URL ||
    "https://customer-assets.emergentagent.com/job_carwash-booking-55/artifacts/6sxfo3tg_LP-1.jpg";

function formatPrice(eur) {
    if (eur == null || isNaN(eur)) return "—";
    return `€${Number(eur).toFixed(2).replace(".", ",")}`;
}

export default function Landing() {
    const site = useSiteInfo();
    const [services, setServices] = useState([]);
    useEffect(() => {
        api.get("/services")
            .then((r) => setServices(r.data || []))
            .catch(() => {});
    }, []);
    const priceExterior = formatPrice(
        services.find((s) => s.id === "exterior")?.price_eur,
    );
    const priceFull = formatPrice(
        services.find((s) => s.id === "interior_exterior")?.price_eur,
    );
    return (
        <div className="min-h-screen flex flex-col" data-testid="landing-page">
            {/* Top bar */}
            <header className="flex items-center justify-between px-6 md:px-12 lg:px-16 py-6 border-b border-[#e5e5e5] gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <Logo />
                    <SiteTag name={site.name} city={site.city} />
                </div>
                <div className="flex items-center gap-6">
                    <Link
                        to="/lookup"
                        className="text-sm text-[#555] hover:text-[#0A1F44] transition-colors"
                        data-testid="lookup-link"
                    >
                        Vind mijn boeking
                    </Link>
                    <Link
                        to="/book"
                        className="bg-[#ED6A14] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C9550C] transition-colors"
                        data-testid="header-book-btn"
                    >
                        Boek een wasbeurt
                    </Link>
                </div>
            </header>

            {/* Hero image — height reduced ~1/3 (more from top, less from bottom) */}
            <div
                className="relative w-full border-b border-[#e5e5e5] overflow-hidden bg-[#0A1F44] aspect-[16/6]"
                data-testid="hero-image-wrap"
            >
                <img
                    src={HERO_IMG}
                    alt={`${site.name} ${site.city} luchtfoto`}
                    className="w-full h-full object-cover object-[center_70%] block"
                    data-testid="hero-image"
                />
            </div>

            {/* Hero text */}
            <section className="border-b border-[#e5e5e5]">
                <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24 max-w-5xl">
                    <span className="text-sm tracking-widest text-[#ED6A14] uppercase mb-6 font-semibold block">
                        Exclusief voor {site.name} · {site.city}
                    </span>
                    <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-[#0A1F44]">
                        Propere wagens
                        <br />
                        <span className="text-[#ED6A14]">op {site.name}.</span>
                    </h1>
                    <p className="mt-8 text-lg text-[#555] max-w-xl leading-relaxed">
                        De mobiele carwash van Cargides Group, exclusief voor
                        medewerkers en bedrijven op {site.name} in {site.city}.
                        Kies een tijdslot en parkeer je wagen — wij doen de rest.
                        De factuur volgt achteraf.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link
                            to="/book"
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-8 py-4 font-semibold hover:bg-[#C9550C] transition-colors"
                            data-testid="hero-book-btn"
                        >
                            Boek je wasbeurt <ArrowRight size={18} />
                        </Link>
                        <a
                            href="#services"
                            className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] px-8 py-4 font-semibold hover:bg-[#0A1F44] hover:text-white transition-colors"
                            data-testid="hero-learn-btn"
                        >
                            Bekijk diensten
                        </a>
                    </div>

                    <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
                        <Stat
                            value="2"
                            label="Wasspecialisten op locatie"
                            testId="stat-workers"
                        />
                        <Stat
                            value="20min"
                            label="Snelle exterieurwas"
                            testId="stat-express"
                        />
                        <Stat
                            value="60min"
                            label="Volledig interieur &amp; exterieur"
                            testId="stat-full"
                        />
                    </div>
                </div>
            </section>

            {/* Services */}
            <section
                id="services"
                className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-[#e5e5e5]"
            >
                <div className="max-w-3xl mb-16">
                    <span className="text-sm tracking-widest text-[#555] uppercase">
                        Twee diensten. Geen gedoe.
                    </span>
                    <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight mt-4 text-[#0A1F44]">
                        Kies wat je wagen vandaag nodig heeft.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#e5e5e5]">
                    <ServiceCard
                        tag="EXPRESS"
                        title="Exterieur reiniging"
                        duration="20 minuten"
                        price={priceExterior}
                        bullets={[
                            "Schuim-prewash & spoeling",
                            "Handwas met zachte wasmitt",
                            "Velgen, banden & wielkasten",
                            "Vlekvrije droogfinish",
                        ]}
                        img="https://images.pexels.com/photos/6873174/pexels-photo-6873174.jpeg"
                        testId="service-exterior"
                    />
                    <ServiceCard
                        tag="VOLLEDIG"
                        title="Interieur &amp; Exterieur"
                        duration="60 minuten"
                        price={priceFull}
                        bullets={[
                            "Alles uit de Express",
                            "Stofzuigen van tapijten & zetels",
                            "Dashboard, deuren & middenconsole",
                            "Glas reinigen binnen én buiten",
                        ]}
                        img="https://images.unsplash.com/photo-1732357624591-f2137085659b?auto=format&fit=crop&w=1200&q=70"
                        testId="service-complete"
                    />
                </div>
            </section>

            {/* How it works */}
            <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-[#e5e5e5]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div>
                        <span className="text-sm tracking-widest text-[#555] uppercase">
                            Zo werkt het
                        </span>
                        <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight mt-4 text-[#0A1F44]">
                            Geboekt &amp; gewassen in geen tijd.
                        </h2>
                    </div>
                    <div className="space-y-10">
                        <Step
                            n="01"
                            title="Kies een dienst"
                            body="Express exterieur (20 min) of volledig interieur & exterieur (60 min)."
                        />
                        <Step
                            n="02"
                            title="Selecteer datum & tijdslot"
                            body="Voor- of namiddag op een dag dat we bij jouw site langskomen."
                        />
                        <Step
                            n="03"
                            title="Wij wassen, jij krijgt de factuur"
                            body="Na de wasbeurt sturen we een factuur door die je achteraf betaalt — geen online betaling vooraf."
                        />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0A1F44] text-white px-6 md:px-12 lg:px-16 py-20 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <SiteTag
                            name={site.name}
                            city={site.city}
                            tone="light"
                            className="mb-6"
                        />
                        <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tighter">
                            Klaar voor een
                            <br />
                            <span className="text-[#ED6A14]">propere wagen?</span>
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/book"
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-8 py-4 font-semibold hover:bg-[#C9550C] transition-colors"
                            data-testid="cta-book-btn"
                        >
                            Reserveer je tijdslot <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
                <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between text-sm text-white/60 flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={16} /> Factuur achteraf · Enkel voor{" "}
                        {site.name} {site.city}
                    </div>
                    <div className="flex items-center gap-6">
                        <Link
                            to="/admin"
                            className="text-white/60 hover:text-white transition-colors"
                            data-testid="admin-link"
                        >
                            Admin
                        </Link>
                        <span>© {new Date().getFullYear()} Cargides Group</span>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Stat({ value, label, testId }) {
    return (
        <div data-testid={testId}>
            <p className="font-display font-bold text-3xl md:text-4xl text-[#0A1F44]">
                {value}
            </p>
            <p
                className="text-sm text-[#555] mt-1 leading-snug"
                dangerouslySetInnerHTML={{ __html: label }}
            />
        </div>
    );
}

function ServiceCard({ tag, title, duration, price, bullets, img, testId }) {
    return (
        <div
            className="bg-white p-8 md:p-10 flex flex-col"
            data-testid={testId}
        >
            <div className="aspect-[16/10] w-full overflow-hidden mb-8">
                <img
                    src={img}
                    alt={title}
                    className="w-full h-full object-cover"
                />
            </div>
            <span className="text-xs tracking-widest text-[#ED6A14] uppercase font-semibold">
                {tag} · {duration}
            </span>
            <h3
                className="font-display font-bold text-3xl tracking-tight mt-3 text-[#0A1F44]"
                dangerouslySetInnerHTML={{ __html: title }}
            />
            <p className="font-display font-bold text-4xl mt-4 text-[#0A1F44]">
                {price}
            </p>
            <p className="text-sm text-[#555] mt-1">Inclusief 21% btw</p>
            <ul className="mt-8 space-y-3 text-[15px]">
                {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 bg-[#ED6A14] shrink-0" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <div className="border-t border-[#e5e5e5] pt-6">
            <div className="flex items-baseline gap-6">
                <span className="font-mono-ui text-sm text-[#ED6A14]">{n}</span>
                <div>
                    <h3 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                        {title}
                    </h3>
                    <p className="text-[#555] mt-2 leading-relaxed">{body}</p>
                </div>
            </div>
        </div>
    );
}
